import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Upload, FileText, Trash2, Download, CheckCircle,
  XCircle, Clock, Loader2, ChevronDown, ChevronRight,
  AlertCircle, Layers
} from "lucide-react";

const STATUS_CONFIG = {
  queued: { label: "Queued", icon: Clock, cls: "bg-slate-100 text-slate-500" },
  processing: { label: "Processing", icon: Loader2, cls: "bg-blue-50 text-blue-600 animate-spin" },
  completed: { label: "Completed", icon: CheckCircle, cls: "bg-emerald-50 text-emerald-600" },
  completed_with_errors: { label: "Done w/ Errors", icon: AlertCircle, cls: "bg-amber-50 text-amber-600" },
  failed: { label: "Failed", icon: XCircle, cls: "bg-red-50 text-red-600" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.queued;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>
      <Icon size={10} className={status === "processing" ? "animate-spin" : ""} />
      {cfg.label}
    </span>
  );
}

function JobRow({ job, schemas, onDelete, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const schema = schemas.find(s => s.id === job.schema_id);
  const pct = job.total_files > 0 ? Math.round(((job.processed_files + job.failed_files) / job.total_files) * 100) : 0;

  const handleDownload = async () => {
    try {
      const resp = await api.get(`/bulk/jobs/${job.id}/csv`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${job.name.replace(/\s+/g, "_")}_${job.id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const isActive = job.status === "queued" || job.status === "processing";

  return (
    <div data-testid={`bulk-job-${job.id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <button onClick={() => setExpanded(!expanded)}
              className="mt-0.5 text-slate-400 hover:text-slate-600 flex-shrink-0">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 text-sm truncate">{job.name}</span>
                <StatusBadge status={job.status} />
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-400 flex-wrap">
                <span>Schema: <span className="text-slate-600">{schema?.name || job.schema_id.slice(0, 8)}</span></span>
                <span>{job.total_files} file{job.total_files !== 1 ? "s" : ""}</span>
                {job.processed_files > 0 && <span className="text-emerald-600">{job.processed_files} done</span>}
                {job.failed_files > 0 && <span className="text-red-500">{job.failed_files} failed</span>}
                <span>{job.created_at?.slice(0, 10)}</span>
              </div>
              {isActive && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#006080] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-slate-400">{pct}%</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {(job.status === "completed" || job.status === "completed_with_errors") && (
              <button onClick={handleDownload} data-testid={`download-csv-${job.id}`} title="Download CSV"
                className="flex items-center gap-1 h-7 px-2.5 text-xs font-medium border border-[#006080] text-[#006080] rounded-md hover:bg-[#E5F0F2] transition-colors">
                <Download size={11} /> CSV
              </button>
            )}
            {isActive && (
              <button onClick={onRefresh} title="Refresh status"
                className="flex items-center h-7 px-2 text-xs border border-slate-200 rounded-md hover:bg-slate-50 text-slate-400">
                <Loader2 size={11} className="animate-spin" />
              </button>
            )}
            <button onClick={() => onDelete(job.id)} data-testid={`delete-job-${job.id}`}
              className="flex items-center h-7 px-2 text-xs border border-slate-200 rounded-md hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors text-slate-400">
              <Trash2 size={11} />
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50">
          <div className="p-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-400 border-b border-slate-100">
                  <th className="text-left pb-2 font-medium">File</th>
                  <th className="text-left pb-2 font-medium">Status</th>
                  <th className="text-left pb-2 font-medium">Fields Extracted</th>
                  <th className="text-left pb-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {(job.files || []).map((f) => (
                  <tr key={f.id} className="border-b border-slate-100/50">
                    <td className="py-1.5 pr-4 text-slate-700 font-mono max-w-[180px] truncate">{f.filename}</td>
                    <td className="py-1.5 pr-4">
                      <StatusBadge status={f.status} />
                    </td>
                    <td className="py-1.5 pr-4 text-slate-600">
                      {Object.keys(f.extracted_fields || {}).length} field{Object.keys(f.extracted_fields || {}).length !== 1 ? "s" : ""}
                    </td>
                    <td className="py-1.5 text-red-500 max-w-[200px] truncate">{f.error || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function BulkProcessing() {
  const { user } = useAuth();
  const [schemas, setSchemas] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [form, setForm] = useState({ name: "", schema_id: "", webhook_id: "" });
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef();
  const pollRef = useRef(null);

  const fetchAll = useCallback(() => {
    Promise.all([
      api.get("/schemas"),
      api.get("/webhooks"),
      api.get("/bulk/jobs"),
    ]).then(([s, w, j]) => {
      setSchemas(s.data.filter(sc => sc.status === "approved"));
      setWebhooks(w.data.filter(wh => wh.is_active));
      setJobs(j.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-poll while any job is processing
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === "queued" || j.status === "processing");
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(() => {
        api.get("/bulk/jobs").then(r => setJobs(r.data)).catch(() => {});
      }, 3000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [jobs]);

  const addFiles = (newFiles) => {
    const valid = Array.from(newFiles).filter(f =>
      ["application/pdf", "image/jpeg", "image/jpg", "image/png"].includes(f.type)
    );
    setFiles(prev => {
      const names = new Set(prev.map(f => f.name));
      return [...prev, ...valid.filter(f => !names.has(f.name))];
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) { setError("Add at least one file."); return; }
    if (!form.schema_id) { setError("Select a schema."); return; }
    setError("");
    setSubmitting(true);

    try {
      // Read all files as base64
      const filePayloads = await Promise.all(
        files.map(f => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = ev => resolve({
            filename: f.name,
            mime_type: f.type,
            file_base64: ev.target.result.split(",")[1],
          });
          reader.onerror = reject;
          reader.readAsDataURL(f);
        }))
      );

      await api.post("/bulk/jobs", {
        name: form.name || `Batch ${new Date().toLocaleString()}`,
        schema_id: form.schema_id,
        webhook_id: form.webhook_id || null,
        files: filePayloads,
      });

      setFiles([]);
      setForm({ name: "", schema_id: "", webhook_id: "" });
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start job");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/bulk/jobs/${id}`);
    setJobs(prev => prev.filter(j => j.id !== id));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Bulk Processing</h2>
        <p className="text-sm text-slate-500 mt-0.5">Upload multiple documents to extract fields in batch and download results as CSV.</p>
      </div>

      {/* Upload Form */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">New Batch Job</h3>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            <AlertCircle size={14} className="flex-shrink-0" /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Job Name (optional)</label>
              <input
                data-testid="bulk-job-name-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={`Batch ${new Date().toLocaleDateString()}`}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Schema <span className="text-red-400">*</span></label>
              <select
                data-testid="bulk-schema-select"
                value={form.schema_id}
                onChange={e => setForm(f => ({ ...f, schema_id: e.target.value }))}
                required
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
              >
                <option value="">Select schema…</option>
                {schemas.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Webhook (optional)</label>
              <select
                data-testid="bulk-webhook-select"
                value={form.webhook_id}
                onChange={e => setForm(f => ({ ...f, webhook_id: e.target.value }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
              >
                <option value="">No webhook</option>
                {webhooks.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          </div>

          {/* Drop zone */}
          <div
            data-testid="bulk-dropzone"
            onDragEnter={() => setDragActive(true)}
            onDragLeave={() => setDragActive(false)}
            onDragOver={e => { e.preventDefault(); setDragActive(true); }}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${dragActive ? "border-[#006080] bg-[#E5F0F2]" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}
          >
            <Upload size={24} className="text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-600">
              Drop PDF, JPG, or PNG files here
            </p>
            <p className="text-xs text-slate-400 mt-1">or click to browse</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={e => addFiles(e.target.files)}
              className="hidden"
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">{files.length} file{files.length !== 1 ? "s" : ""} selected</span>
                <button type="button" onClick={() => setFiles([])} className="text-xs text-slate-400 hover:text-red-500">Clear all</button>
              </div>
              <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={12} className="text-[#006080] flex-shrink-0" />
                      <span className="text-xs text-slate-700 truncate">{f.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
                      <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                        className="text-slate-300 hover:text-red-500 transition-colors">
                        <XCircle size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="bulk-submit-button"
              disabled={submitting || !files.length}
              className="flex items-center gap-2 h-9 px-5 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50 transition-colors"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Processing…</> : <><Layers size={14} /> Start Batch</>}
            </button>
          </div>
        </form>
      </div>

      {/* Jobs list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">Batch Jobs</h3>
          <button onClick={fetchAll} className="text-xs text-slate-400 hover:text-[#006080] transition-colors">Refresh</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
            <Layers size={28} className="text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No batch jobs yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {jobs.map(job => (
              <JobRow
                key={job.id}
                job={job}
                schemas={schemas}
                onDelete={handleDelete}
                onRefresh={fetchAll}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
